import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { APP_ENV } from 'apps/cmg-clear2-ui/src/environments/environment.model';
import { filter } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class AppInsightsLoggerService {
  private readonly _router = inject(Router);
  private readonly _environment = inject(APP_ENV);

  private readonly _angularPlugin = new AngularPlugin();
  private readonly _appInsights = new ApplicationInsights({
    config: {
      connectionString: this._environment.appInsights.connectionString,
      enableAutoRouteTracking: true,
      enableCorsCorrelation: true,
      extensions: [this._angularPlugin],
      extensionConfig: {
        [this._angularPlugin.identifier]: {
          router: this._router,
        },
      },
    },
  });

  constructor() {
    if (this._environment.appInsights.excludedRegexes) {
      this._appInsights.config.excludeRequestFromAutoTrackingPatterns = this._environment.appInsights.excludedRegexes;
    }

    this._appInsights.loadAppInsights();
    this._loadCustomTelemetryProperties();
    this._createRouterSubscription();
  }

  logPageView(name?: string, url?: string) {
    // option to call manually
    this._appInsights.trackPageView({
      name: name,
      uri: url,
    });
  }

  logEvent(name: string, properties?: { [key: string]: any }) {
    this._appInsights.trackEvent({ name: name }, properties);
  }

  logMetric(name: string, average: number, properties?: { [key: string]: any }) {
    this._appInsights.trackMetric({ name: name, average: average }, properties);
  }

  logException(exception: Error, severityLevel?: number) {
    this._appInsights.trackException({ exception: exception, severityLevel: severityLevel });
  }

  logTrace(message: string, properties?: { [key: string]: any }) {
    this._appInsights.trackTrace({ message: message }, properties);
  }

  private _createRouterSubscription() {
    this._router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: NavigationEnd) => {
      this.logPageView(null, event.urlAfterRedirects);
    });
  }
  private _loadCustomTelemetryProperties() {
    this._appInsights.addTelemetryInitializer((envelope) => {
      const item = envelope.baseData;
      item.properties = item.properties || {};
      item.properties['ApplicationPlatform'] = 'Web';
      item.properties['ApplicationName'] = 'Clear 2.0 Web';
      envelope.tags = envelope.tags || [];
      envelope.tags.push({ 'ai.cloud.role': this._environment.appInsights.applicationName }); // this app name is used for application map
    });
  }
}
